import { useState } from 'react';
import type { MemberLeave } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useMemberLeave, useSetLeave, useEndLeave, useDeleteLeave, type LeaveSaved } from '../lib/queries.js';
import { shortDate } from '../lib/shifts.js';

/** "Sat 12 Oct" for a local YYYY-MM-DD. */
export function leaveDay(ymd: string): string {
  return new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Short tag: "On leave until Fri 16 Oct", "Paused", "Leave from Mon 2 Nov". */
export function leaveLabel(l: MemberLeave): string {
  if (!l.active) return `Leave from ${leaveDay(l.startsOn)}`;
  return l.endsOn ? `On leave until ${leaveDay(l.endsOn)}` : 'Paused';
}

/** Leave overlapping [from, to] (local YYYY-MM-DD, inclusive). */
export function leaveDuring(l: MemberLeave | null | undefined, from: string, to: string): boolean {
  return !!l && l.startsOn <= to && (!l.endsOn || l.endsOn >= from);
}

/** Small amber (active) or grey (planned) tag. */
export function LeaveTag({ leave }: { leave: MemberLeave }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, whiteSpace: 'nowrap',
      background: leave.active ? theme.status.warn.bg : theme.surface2,
      color: leave.active ? theme.status.warn.fg : theme.textDim, border: `1px solid ${theme.border}`,
    }}>{leaveLabel(leave)}</span>
  );
}

const localToday = () => new Date().toLocaleDateString('en-CA');

/**
 * A member's leave: current and planned, put on leave, back today, remove.
 * Admins and the member's team leader/deputy (the API checks).
 */
export function LeaveCard({ orgId, userId, name }: { orgId: string; userId: string; name: string }) {
  const { data: list = [], isLoading } = useMemberLeave(orgId, userId);
  const setMut = useSetLeave(orgId, userId);
  const endMut = useEndLeave(orgId, userId);
  const delMut = useDeleteLeave(orgId, userId);
  const [form, setForm] = useState<null | { id?: string; startsOn: string; endsOn: string; open: boolean; reason: string }>(null);
  const [saved, setSaved] = useState<LeaveSaved | null>(null);
  const live = list.filter((l) => !l.past);
  const first = name.split(' ')[0];
  const error = (setMut.error ?? endMut.error ?? delMut.error) as Error | null;

  function save() {
    if (!form) return;
    setMut.mutate(
      { id: form.id, startsOn: form.startsOn, endsOn: form.open ? null : form.endsOn || null, reason: form.reason.trim() || null },
      { onSuccess: (r) => { setSaved(r); setForm(null); } },
    );
  }

  return (
    <div style={box}>
      <div style={label}>Leave</div>
      {isLoading && <div style={muted}>Loading…</div>}
      {!isLoading && !live.length && !form && <div style={muted}>Not on leave.</div>}
      {live.map((l) => (
        <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: `1px solid ${theme.border}` }}>
          <div style={{ flex: '1 1 180px', minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: l.active ? theme.status.warn.fg : theme.text }}>
              {l.active ? (l.endsOn ? 'On leave' : 'Paused') : 'Planned'}
              <span style={{ fontWeight: 500, color: theme.textDim }}>
                {' · '}{leaveDay(l.startsOn)}{l.endsOn ? ` – ${leaveDay(l.endsOn)}` : ', until further notice'}
              </span>
            </div>
            {l.reason && <div style={{ fontSize: 12, color: theme.textMuted }}>{l.reason}</div>}
          </div>
          <button type="button" style={btn} onClick={() => setForm({ id: l.id, startsOn: l.startsOn, endsOn: l.endsOn ?? '', open: !l.endsOn, reason: l.reason ?? '' })}>Change</button>
          {l.active
            ? <button type="button" style={btn} disabled={endMut.isPending} onClick={() => endMut.mutate(l.id)}>Back today</button>
            : <button type="button" style={btn} disabled={delMut.isPending} onClick={() => delMut.mutate(l.id)}>Remove</button>}
        </div>
      ))}

      {form ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8, borderTop: `1px solid ${theme.border}` }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={field}>First day
              <input type="date" value={form.startsOn} onChange={(e) => setForm({ ...form, startsOn: e.target.value })} style={input} />
            </label>
            <label style={{ ...field, opacity: form.open ? 0.5 : 1 }}>Last day
              <input type="date" value={form.endsOn} min={form.startsOn} disabled={form.open}
                onChange={(e) => setForm({ ...form, endsOn: e.target.value })} style={input} />
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.text }}>
            <input type="checkbox" checked={form.open} onChange={(e) => setForm({ ...form, open: e.target.checked })} />
            Until further notice (paused)
          </label>
          <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} maxLength={300}
            placeholder="Reason (optional — admins and team leaders only)" aria-label="Reason" style={input} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={btn} onClick={() => setForm(null)}>Cancel</button>
            <button type="button" style={primary} disabled={setMut.isPending || !form.startsOn || (!form.open && !form.endsOn)} onClick={save}>
              {setMut.isPending ? 'Saving…' : form.id ? 'Save' : 'Put on leave'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted }}>
            While on leave {first} isn't counted on the crew, isn't asked to cover, gets no notifications,
            and their ops support shifts go back to the team. Their team leaders are told.
          </div>
        </div>
      ) : (
        <button type="button" style={{ ...btn, alignSelf: 'flex-start', marginTop: 4 }}
          onClick={() => { setSaved(null); setForm({ startsOn: localToday(), endsOn: '', open: false, reason: '' }); }}>
          {live.length ? 'Add more leave' : 'Put on leave'}
        </button>
      )}

      {saved && (saved.conflicts.length > 0 || saved.releasedOps > 0) && (
        <div style={{ fontSize: 12, color: theme.status.warn.fg, paddingTop: 4 }}>
          {saved.releasedOps > 0 && <div>{saved.releasedOps} ops support shift{saved.releasedOps > 1 ? 's' : ''} went back to the team.</div>}
          {saved.conflicts.map((c) => (
            <div key={c.periodStart + c.kind}>
              {first} is {c.kind === 'udo_day' ? 'day UDO' : 'UDO'} in the week of {shortDate(c.periodStart)} — pick someone else on Admin → Roster.
            </div>
          ))}
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: theme.status.danger.fg }}>{error.message}</div>}
    </div>
  );
}

const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 18px', background: theme.surface2, borderBottom: `1px solid ${theme.border}` };
const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted };
const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted };
const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: theme.textDim, flex: '1 1 140px' };
const input: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 14, boxSizing: 'border-box', width: '100%',
};
const btn: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const primary: React.CSSProperties = { ...btn, background: theme.accent, border: 'none', color: theme.accentInk, flex: 1 };
